<template>
  <div class="i-select-plan-countdown-nav">
    <div class="inner">
      Complete within
      <div class="bold">{{ formattedTime }}</div>
      or your
      <div
        v-if="dateFromURL"
        class="bold"
      >
        {{ BOOKED_DATE }} {{ BOOKED_TIME }}
      </div>
      <template v-else>booked</template>
      spot will be released.
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, reactive, onBeforeUnmount } from "vue"
import { useRoute } from "vue-router"
import { useCoachingStore } from "@/stores/coaching"

// EMITS
const emit = defineEmits(["updateCountdown"])

// STORES AND ROUTES
const coachingStore = useCoachingStore()
const activeRoute = useRoute()
const dateFromURL = activeRoute.query["date"]?.toString()
const dateTimeObject = new Date(dateFromURL || "")

// CONSTANTS
const TIMER_DURATION_MIN = 15

const BOOKED_DATE = new Date(dateTimeObject).toLocaleDateString("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric"
})

const BOOKED_TIME = new Date(dateTimeObject).toLocaleTimeString("en-US", {
  hour: "2-digit",
  minute: "2-digit",
  timeZoneName: "short"
})

// REFS
const timeRemaining = ref(TIMER_DURATION_MIN * 60)
const data = reactive({
  showInfoModal: false,
  interval: undefined as undefined | ReturnType<typeof setInterval>
})

// COMPUTED
const formattedTime = computed(() => {
  const minutes = Math.floor((timeRemaining.value / 60) % 60)
  const seconds = Math.floor(timeRemaining.value % 60)

  return `${minutes < 1 ? "" : minutes == 1 ? "1 min" : minutes + " mins"} ${seconds < 10 ? "0" + seconds : seconds} secs`
})
const assignedCoachFullName = computed(() => coachingStore.getAssignedCoachFullName || "Coach")

// FUNCTIONS
function startTimer() {
  data.interval = setInterval(() => {
    if (timeRemaining.value > 0) timeRemaining.value--

    if (timeRemaining.value >= 0) {
      localStorage.setItem("coaching-countdown", timeRemaining.value.toString())
    }

    if (timeRemaining.value <= 0) {
      clearInterval(data.interval)
    }

    emit("updateCountdown", timeRemaining.value)
  }, 1000)
}

const setStartTime = async () => {
  const timer = localStorage.getItem("coaching-countdown")
  const previousBookingDetails = localStorage.getItem("select-plan-booking-details") || ``
  const newBookingDetails = `${assignedCoachFullName.value}+${BOOKED_DATE}+${BOOKED_TIME}`

  if (timer == null || previousBookingDetails != newBookingDetails) {
    localStorage.setItem("coaching-countdown", timeRemaining.value.toString())
    localStorage.setItem("select-plan-booking-details", newBookingDetails)
  }

  timeRemaining.value = parseInt(localStorage.getItem("coaching-countdown") || timeRemaining.value.toString())
}

// LIFECYCLE HOOKS
onMounted(async () => {
  await setStartTime()
  startTimer()
})

onBeforeUnmount(() => {
  clearInterval(data.interval)
})
</script>

<style lang="scss" scoped>
.i-select-plan-countdown-nav {
  width: 100%;
  background-image: linear-gradient(0deg, #0009 0%, #0009 100%), linear-gradient(90deg, #068eff 0%, #ab2af3 100%);
  padding: 0 5px;

  @media screen and (min-width: $breakpoint-sm) {
    padding: 0 10px;
  }

  @media screen and (min-width: $breakpoint-md) {
    padding: 0 20px;
  }

  .inner {
    @include flex(row, center, center, wrap, 2px 5px);
    @include text-xs;

    color: $white;
    max-width: 1152px;
    margin: 0 auto;
    padding: 8px 0;
    font-weight: 300;

    @media screen and (min-width: $breakpoint-sm) {
      @include text-sm;

      padding: 15px 0;
    }

    .bold {
      font-weight: 700;
      min-width: 94px;
      text-align: center;

      @media screen and (min-width: $breakpoint-sm) {
        min-width: 110px;
      }
    }
  }
}
</style>
